import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import { getMyBusinessVerificationStatus, submitBusinessVerification } from '../api/businessVerification'

const BusinessVerificationPage = () => {
  const navigate = useNavigate()
  const { user, refreshUser } = useUser()

  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    organizationName: '',
    website: '',
    ein: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const s = await getMyBusinessVerificationStatus()
      setStatus(s)
    } catch (e: any) {
      setError(e?.message || 'Failed to load verification status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user && user.userType !== 'FOOD_BANK' && user.userType !== 'SUPPLIER') {
      navigate('/')
      return
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userType])

  const submit = async () => {
    try {
      setSaving(true)
      setError(null)
      await submitBusinessVerification({
        organizationName: form.organizationName,
        website: form.website || undefined,
        ein: form.ein || undefined,
        street: form.street,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode
      })
      await refreshUser()
      await load()
    } catch (e: any) {
      setError(e?.message || 'Failed to submit verification')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <h1 className="text-xl font-bold text-gray-900">Business Verification</h1>
        <p className="text-sm text-gray-600 mt-1">Required for suppliers and food banks before admin approval.</p>

        {loading ? (
          <p className="text-sm text-gray-600 mt-4">Loading…</p>
        ) : (
          <>
            {status && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-800">
                Status: <span className="font-semibold">{status.status}</span>
                {status.status === 'REJECTED' && status.rejectionReason ? ` — ${status.rejectionReason}` : ''}
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">{error}</div>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                <input
                  value={form.organizationName}
                  onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website (optional)</label>
                <input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">EIN (optional)</label>
                <input
                  value={form.ein}
                  onChange={(e) => setForm({ ...form, ein: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
                <input
                  value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                <input
                  value={form.zipCode}
                  onChange={(e) => setForm({ ...form, zipCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={submit}
                disabled={saving}
                className={`btn-primary ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Submitting…' : 'Submit for review'}
              </button>
              <button onClick={() => navigate('/')} className="btn-outline">
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default BusinessVerificationPage


